import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import Select from "@/components/Select";
import SegmentedButton from "@/components/SegmentedButton";
import TextField from "@/components/TextField";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate, type Path } from "@/router";
import {
  ACTIVE_CALL_SESSION_CHANGED_EVENT,
  clearActiveCallSelectionForCallSid,
  markActiveCallEndedForCallSid,
  readActiveCallSelection,
  type ActiveCallSelection
} from "@/layouts/AppLayout/IncomingCallEventIntegration/sessionStorage";
import {
  loadStagedSummary,
  loadSummaryHistory,
  loadSummarySettings,
  promoteSummaryEntry,
  pushSummaryHistory,
  removeSummaryHistoryEntry,
  restoreSeedSummaries,
  saveStagedSummary,
  saveSummarySettings,
  type EmulatorSummaryEntry,
  type EmulatorSummarySettings
} from "./summaries";
import useEmulatorRequestOptions from "./useEmulatorRequestOptions";
import useFlexEmulatorSession from "./useFlexEmulatorSession";
import { incomingCallPathFor, setFlexEmulatorSessionActive, wrapUpPathFor } from "./session";
import {
  completeEmulatorCall,
  EMULATOR_BACKEND_NOT_ENABLED_MESSAGE,
  EMULATOR_TRANSCRIPT_STATUS_OPTIONS,
  fetchEmulatorCall,
  fetchRecentCalls,
  fetchSampleCaller,
  FlexEmulatorError,
  isEmulatorNotEnabledError,
  setEmulatorTranscript,
  startEmulatorCall,
  type EmulatorCall,
  type EmulatorDirection
} from "./api";

// Twilio brand red — intentionally not a sys-brand token.
const TWILIO_RED = "#F22F46";
// Above the side-sheet stack (drawer portal: scrim 1000, sheet 1001, tab 1002)
// so dock clicks never hit the dismiss scrim while a flyout is open.
const DOCK_Z_INDEX_CLASS = "z-[2000]";

const DIRECTION_OPTIONS = [
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound-api" }
];

type DockBusyKey = "load" | "end" | "transcript" | "start" | "sample" | "recent";

const describeError = (err: unknown): string => {
  if (isEmulatorNotEnabledError(err)) return EMULATOR_BACKEND_NOT_ENABLED_MESSAGE;
  if (err instanceof FlexEmulatorError) {
    if (err.status === 401) return "Your session expired. Please log in again.";
    return err.message;
  }
  return "Something went wrong. Please try again.";
};

const interactionPathFor = (pgaId: string, interactionId: string): Path =>
  `/people/${encodeURIComponent(pgaId)}/interactions/interactions/${interactionId}` as Path;

const previewSnippet = (text: string): string => (text.length > 90 ? `${text.slice(0, 90)}…` : text);

const formatCallSid = (callSid: string) =>
  callSid.length <= 17 ? callSid : `${callSid.slice(0, 13)}…${callSid.slice(-4)}`;

/**
 * The whole emulator in one floating panel (DEV builds, emulator session
 * only): start calls, drive wrap-up, inject AI summaries, curate the summary
 * library, and browse recent calls — without leaving the product UI. Keeps the
 * last call around after the agent flow clears its selection so late summaries
 * can still be injected post-finalize.
 */
export default function FlexEmulatorDock() {
  const session = useFlexEmulatorSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const requestOptions = useEmulatorRequestOptions();
  const [selection, setSelection] = useState<ActiveCallSelection | null>(null);
  const [lastCallSid, setLastCallSid] = useState<string | null>(null);
  const [lastPgaId, setLastPgaId] = useState<string | null>(null);
  const [call, setCall] = useState<EmulatorCall | null>(null);
  const [phone, setPhone] = useState("");
  const [direction, setDirection] = useState<EmulatorDirection>("inbound");
  const [initialStaged] = useState(loadStagedSummary);
  const [transcriptStatus, setTranscriptStatus] = useState(initialStaged.status);
  const [summaryText, setSummaryText] = useState(initialStaged.text);
  const [history, setHistory] = useState<EmulatorSummaryEntry[]>(() => loadSummaryHistory());
  const [initialSettings] = useState(loadSummarySettings);
  const [settings, setSettings] = useState(initialSettings);
  const [delaySeconds, setDelaySeconds] = useState(() => String(initialSettings.delaySeconds || ""));
  const [recentCalls, setRecentCalls] = useState<EmulatorCall[]>([]);
  // In-flight action keys (a set, not a single enum) so overlapping loads
  // can't clear each other's busy state early.
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<DockBusyKey>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const startBusy = useCallback((key: DockBusyKey) => {
    setBusyKeys((current) => (current.has(key) ? current : new Set(current).add(key)));
  }, []);

  const endBusy = useCallback((key: DockBusyKey) => {
    setBusyKeys((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!session) return;

    const refreshSelection = () => setSelection(readActiveCallSelection());
    refreshSelection();
    window.addEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
    window.addEventListener("storage", refreshSelection);

    return () => {
      window.removeEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
      window.removeEventListener("storage", refreshSelection);
    };
  }, [session]);

  useEffect(() => {
    if (selection?.callSid) {
      setLastCallSid(selection.callSid);
    }
    if (selection?.pgaId) {
      setLastPgaId(selection.pgaId);
    }
  }, [selection?.callSid, selection?.pgaId]);

  const loadRecentCalls = useCallback(async () => {
    startBusy("recent");
    setError(null);
    try {
      setRecentCalls(await fetchRecentCalls({ ...requestOptions, limit: 5 }));
    } catch (err) {
      setError(describeError(err));
    } finally {
      endBusy("recent");
    }
  }, [requestOptions, startBusy, endBusy]);

  const loadCall = useCallback(
    async (callSid: string) => {
      startBusy("load");
      setError(null);
      try {
        setCall(await fetchEmulatorCall(callSid, requestOptions));
      } catch (err) {
        setCall(null);
        setError(describeError(err));
      } finally {
        endBusy("load");
      }
    },
    [requestOptions, startBusy, endBusy]
  );

  const activeCallSid = selection?.callSid ?? lastCallSid;

  useEffect(() => {
    if (!session) return;
    void loadRecentCalls();
  }, [session, loadRecentCalls]);

  // Reload when the CallSid changes or the agent flow clears its selection
  // (finalize) so the dock reveals the finalized state. Selection mutations
  // that keep the same CallSid (e.g. the end-flip) skip the reload because the
  // action that caused them already holds fresh state.
  const loadedCallSidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!session || !activeCallSid) {
      setCall(null);
      loadedCallSidRef.current = null;
      return;
    }
    if (selection && loadedCallSidRef.current === activeCallSid) return;
    loadedCallSidRef.current = activeCallSid;
    void loadCall(activeCallSid);
  }, [session, selection, activeCallSid, loadCall]);

  if (!session) return null;

  const updateSettings = (patch: Partial<EmulatorSummarySettings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch };
      saveSummarySettings(next);
      return next;
    });
  };

  const isEnded = !!call?.endedAt;
  const busyAny = busyKeys.size > 0;
  const stagedSummary = summaryText.trim() || history[0]?.text;
  // Prefer the loaded call's own person: lastPgaId can belong to a different
  // agent selection when an old call is loaded into the dock.
  const viewInteractionPgaId = call?.person?.pgaId ?? lastPgaId ?? null;
  const finalizedInteractionId = isEnded ? call?.interactionId : null;

  const handleSampleCaller = () => {
    startBusy("sample");
    setError(null);
    void (async () => {
      try {
        const { caller, hint } = await fetchSampleCaller(requestOptions);
        if (!caller) {
          setError(hint ?? "No person phone numbers found. Enter a phone number manually.");
          return;
        }
        setPhone(caller.phone);
      } catch (err) {
        setError(describeError(err));
      } finally {
        endBusy("sample");
      }
    })();
  };

  const handleStart = () => {
    startBusy("start");
    setError(null);
    void (async () => {
      try {
        // Blank phone means "call me as a random member" so Start never
        // dead-ends on an empty field.
        let dialPhone = phone.trim();
        if (!dialPhone) {
          const { caller, hint } = await fetchSampleCaller(requestOptions);
          if (!caller) {
            setError(hint ?? "No person phone numbers found. Enter a phone number manually.");
            return;
          }
          dialPhone = caller.phone;
          setPhone(caller.phone);
        }
        const started = await startEmulatorCall({ phone: dialPhone, direction }, requestOptions);
        setCall(started);
        setLastCallSid(started.callSid);
        showSnackbar("Emulated call started — ringing the agent UI");
        navigate(incomingCallPathFor(pathname, started.participantPhone ?? dialPhone, started.callSid) as Path);
      } catch (err) {
        setError(describeError(err));
      } finally {
        endBusy("start");
      }
    })();
  };

  // Abandons the dock's working call (agent selection included) so another
  // call can be started or loaded. The backend rows are untouched.
  const handleClearCall = () => {
    if (selection?.callSid) {
      clearActiveCallSelectionForCallSid(selection.callSid);
    }
    setLastCallSid(null);
    setLastPgaId(null);
  };

  const handleEndCall = () => {
    if (!activeCallSid) return;
    const callSid = activeCallSid;
    startBusy("end");
    setError(null);
    void (async () => {
      try {
        const ended = await completeEmulatorCall(callSid, undefined, requestOptions);
        setCall(ended);
        showSnackbar("Emulated call ended");

        // A real hangup reaches the agent UI instantly (the Flex handoff),
        // separately from the backend webhook. Flip the panel in place — but
        // never resurrect an already-finalized wrap-up.
        if (!ended.interactionId) {
          markActiveCallEndedForCallSid(callSid);
        }

        if (settings.autoSummary) {
          const typed = summaryText.trim();
          if (typed) setHistory(pushSummaryHistory(typed));
          const summary = typed || history[0]?.text;
          const result = await setEmulatorTranscript(
            callSid,
            {
              status: transcriptStatus,
              ...(summary ? { summary } : {}),
              ...(settings.delaySeconds > 0 ? { delaySeconds: settings.delaySeconds } : {})
            },
            requestOptions
          );
          setCall(result.call);
          showSnackbar(result.scheduled ? "Summary scheduled" : "Summary applied");
        }
        await loadRecentCalls();
      } catch (err) {
        setError(describeError(err));
      } finally {
        endBusy("end");
      }
    })();
  };

  const handleApplyTranscript = () => {
    if (!activeCallSid) return;
    const callSid = activeCallSid;
    const typed = summaryText.trim();
    if (typed) setHistory(pushSummaryHistory(typed));
    const delay = Math.max(0, Number.parseInt(delaySeconds.trim() || "0", 10) || 0);
    updateSettings({ delaySeconds: delay });
    startBusy("transcript");
    setError(null);
    void (async () => {
      try {
        const result = await setEmulatorTranscript(
          callSid,
          {
            status: transcriptStatus,
            ...(typed ? { summary: typed } : history[0] ? { summary: history[0].text } : {}),
            ...(delay > 0 ? { delaySeconds: delay } : {})
          },
          requestOptions
        );
        setCall(result.call);
        showSnackbar(result.scheduled ? "Summary scheduled" : "Summary applied");
      } catch (err) {
        setError(describeError(err));
      } finally {
        endBusy("transcript");
      }
    })();
  };

  return createPortal(
    <div
      className={`fixed bottom-6 left-6 ${DOCK_Z_INDEX_CLASS} w-[340px] overflow-hidden rounded-xl border border-sys-brand-outline bg-sys-brand-surface-container-low shadow-web-3`}
      data-testid="flex-emulator-dock"
    >
      <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: TWILIO_RED }}>
        <p className="flex items-center gap-2 text-title-small font-bold text-white">
          <img src={`${import.meta.env.BASE_URL}flex-emulator-mark-white.png`} alt="" width={20} height={20} />
          Flex emulator
        </p>
        <button
          type="button"
          onClick={() => setFlexEmulatorSessionActive(false)}
          aria-label="End emulator session"
          title="End emulator session"
          className="cursor-pointer rounded p-1 text-white"
        >
          <Icon name="close" size={20} />
        </button>
      </div>

      <div className="max-h-[75vh] overflow-y-auto">
        <div className="flex flex-col gap-6 p-4">
          {error && <Banner variant="error" description={error} closable onClose={() => setError(null)} />}

          {!selection?.callSid && (
            <section>
              <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">Start a call</p>
              <div className="flex flex-col gap-6">
                <TextField
                  label="Caller phone"
                  placeholder="+14155551212"
                  value={phone}
                  onChangeText={setPhone}
                  supportingText="Blank starts a call from a random member."
                  variant="outlined"
                  className="w-full"
                />
                <div>
                  <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">Direction</p>
                  <SegmentedButton
                    options={DIRECTION_OPTIONS}
                    value={direction}
                    onChange={(value) => {
                      if (value) setDirection(value as EmulatorDirection);
                    }}
                    fullWidth
                    ariaLabel="Call direction"
                  />
                  <p className="mt-2 text-body-small text-sys-brand-on-surface-variant">
                    {direction === "inbound" ? "A member is calling in." : "You are calling a member."}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outlined"
                    onClick={handleSampleCaller}
                    loading={busyKeys.has("sample")}
                    disabled={busyAny}
                  >
                    Sample caller
                  </Button>
                  <Button variant="filled" onClick={handleStart} loading={busyKeys.has("start")} disabled={busyAny}>
                    Start call
                  </Button>
                </div>
              </div>
            </section>
          )}

          {activeCallSid &&
            (!call ? (
              <p className="text-body-medium text-sys-brand-on-surface-variant">
                {busyKeys.has("load") ? "Loading call…" : "Call unavailable."}
              </p>
            ) : (
              <>
                <section>
                  <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">Active call</p>
                  <p className="text-title-small text-sys-brand-on-surface">
                    {call.participantPhone ?? "Unknown caller"}
                  </p>
                  <p className="text-body-small text-sys-brand-on-surface-variant">
                    {formatCallSid(call.callSid)}
                    {" · "}
                    {isEnded ? "Ended" : "In progress"}
                    {" · AI summary "}
                    {call.transcriptSummaryStatus}
                    {call.interactionId ? " · finalized" : ""}
                  </p>
                  {call.transcriptSummary && (
                    <p className="mt-2 text-body-small text-sys-brand-on-surface">
                      {previewSnippet(call.transcriptSummary)}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {!isEnded && (
                      <Button variant="filled" onClick={handleEndCall} loading={busyKeys.has("end")} disabled={busyAny}>
                        End call
                      </Button>
                    )}
                    {!isEnded && activeCallSid !== selection?.callSid && (
                      <Button
                        variant="text"
                        onClick={() =>
                          navigate(incomingCallPathFor(pathname, call.participantPhone, call.callSid) as Path)
                        }
                        disabled={busyAny || !call.participantPhone}
                      >
                        Ring agent UI
                      </Button>
                    )}
                    {isEnded && !call.interactionId && !selection?.ended && (
                      <Button
                        variant="filled"
                        onClick={() => navigate(wrapUpPathFor(pathname, call.callSid) as Path)}
                        disabled={busyAny}
                      >
                        Open wrap-up
                      </Button>
                    )}
                    {isEnded && !call.interactionId && selection?.ended && (
                      <p className="text-body-small text-sys-brand-on-surface-variant">
                        Wrap-up is open in the Call Details panel — finalize there.
                      </p>
                    )}
                    {finalizedInteractionId && viewInteractionPgaId && (
                      <Button
                        variant="text"
                        onClick={() => navigate(interactionPathFor(viewInteractionPgaId, finalizedInteractionId))}
                        disabled={busyAny}
                      >
                        View interaction
                      </Button>
                    )}
                    {selection?.callSid && (
                      <Button variant="text" onClick={handleClearCall} disabled={busyAny}>
                        Clear
                      </Button>
                    )}
                    <Button
                      variant="text"
                      onClick={() => void loadCall(call.callSid)}
                      loading={busyKeys.has("load")}
                      disabled={busyAny}
                    >
                      Refresh
                    </Button>
                  </div>
                </section>

                <section>
                  <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">AI summary</p>
                  <div className="flex flex-col gap-6">
                    <Checkbox
                      name="flex-emulator-auto-summary"
                      label="Auto-send on hangup"
                      checked={settings.autoSummary}
                      onChange={(checked) => updateSettings({ autoSummary: checked })}
                    />
                    <TextField
                      label="Delay after hangup (seconds)"
                      placeholder="0"
                      value={delaySeconds}
                      onChangeText={(value) => {
                        setDelaySeconds(value);
                        updateSettings({ delaySeconds: Math.max(0, Number.parseInt(value.trim() || "0", 10) || 0) });
                      }}
                      supportingText="Finalize first to watch the backfill land."
                      variant="outlined"
                      className="w-full"
                    />
                    <Select
                      label="Outcome"
                      options={EMULATOR_TRANSCRIPT_STATUS_OPTIONS}
                      value={transcriptStatus}
                      onSelect={(value) => {
                        setTranscriptStatus(value);
                        saveStagedSummary({ text: summaryText, status: value });
                      }}
                    />
                    <TextField
                      label="Summary text"
                      value={summaryText}
                      onChangeText={(value) => {
                        setSummaryText(value);
                        saveStagedSummary({ text: value, status: transcriptStatus });
                      }}
                      supportingText="Blank sends the most recent saved summary, or a built-in sample."
                      type="textarea"
                      rows={3}
                      variant="outlined"
                      className="w-full"
                    />
                    <div>
                      <Button
                        variant="outlined"
                        onClick={handleApplyTranscript}
                        loading={busyKeys.has("transcript")}
                        disabled={busyAny || !isEnded}
                      >
                        Apply
                      </Button>
                      {!isEnded && (
                        <p className="mt-2 text-body-small text-sys-brand-on-surface-variant">
                          End the call first — summaries only exist after hangup.
                        </p>
                      )}
                    </div>
                    {stagedSummary && (
                      <p className="-mt-4 text-body-small text-sys-brand-on-surface-variant">
                        Will send: {previewSnippet(stagedSummary)}
                      </p>
                    )}
                  </div>
                </section>
              </>
            ))}

          <section>
            <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">
              Saved summaries ({history.length}/10)
            </p>
            {history.length === 0 ? (
              <div className="flex items-center gap-2">
                <p className="text-body-medium text-sys-brand-on-surface-variant">Library empty.</p>
                <Button variant="text" onClick={() => setHistory(restoreSeedSummaries())}>
                  Restore samples
                </Button>
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-sys-brand-outline">
                {history.map((entry, index) => (
                  <div
                    key={entry.id}
                    className={`flex items-start justify-between gap-2 p-3 ${index > 0 ? "border-t border-sys-brand-outline" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-body-small text-sys-brand-on-surface">{entry.text}</p>
                      <Button
                        variant="text"
                        onClick={() => {
                          setHistory(promoteSummaryEntry(entry.id));
                          setSummaryText(entry.text);
                          saveStagedSummary({ text: entry.text, status: transcriptStatus });
                        }}
                        ariaLabel={`Use saved summary ${index + 1}`}
                      >
                        Use this summary
                      </Button>
                    </div>
                    <IconButton
                      name="close"
                      ariaLabel="Remove saved summary"
                      onClick={() => setHistory(removeSummaryHistoryEntry(entry.id))}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-label-medium text-sys-brand-on-surface-variant">Recent calls</p>
              <Button variant="text" onClick={() => void loadRecentCalls()} loading={busyKeys.has("recent")}>
                Refresh
              </Button>
            </div>
            <p className="mb-2 text-body-small text-sys-brand-on-surface-variant">
              Load drives a call from this dock. Wrap-up opens its finalize flow. Clear the active call to switch.
            </p>
            {recentCalls.length === 0 ? (
              <p className="text-body-medium text-sys-brand-on-surface-variant">
                {busyKeys.has("recent") ? "Loading…" : "No emulated calls yet."}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {recentCalls.map((recent) => (
                  <div
                    key={recent.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sys-brand-outline p-2"
                  >
                    <div className="min-w-0">
                      <p className="text-title-small text-sys-brand-on-surface">
                        {recent.participantPhone ?? "Unknown caller"}
                      </p>
                      <p className="text-body-small text-sys-brand-on-surface-variant">
                        {recent.endedAt ? "ended" : "in progress"}
                        {" · AI summary "}
                        {recent.transcriptSummaryStatus}
                        {recent.interactionId ? " · finalized" : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="text"
                        onClick={() => {
                          setLastCallSid(recent.callSid);
                        }}
                        disabled={busyAny || (!!selection?.callSid && selection.callSid !== recent.callSid)}
                      >
                        Load
                      </Button>
                      {recent.endedAt && !recent.interactionId && (
                        <Button
                          variant="text"
                          onClick={() => {
                            // The wrap-up flow ignores SIDs other than the
                            // stored selection, so abandon it first.
                            if (selection?.callSid && selection.callSid !== recent.callSid) {
                              clearActiveCallSelectionForCallSid(selection.callSid);
                            }
                            navigate(wrapUpPathFor(pathname, recent.callSid) as Path);
                          }}
                        >
                          Wrap-up
                        </Button>
                      )}
                      {recent.interactionId && recent.person?.pgaId && (
                        <Button
                          variant="text"
                          onClick={() => {
                            const personId = recent.person?.pgaId;
                            const targetInteractionId = recent.interactionId;
                            if (personId && targetInteractionId) {
                              navigate(interactionPathFor(personId, targetInteractionId));
                            }
                          }}
                        >
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
